import React, { useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { TooltipProvider, Tooltip as UITooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import DataTablePagination from '@/components/ui/data-table-pagination';
import { ArrowLeft, Loader2, Car, CheckCircle, XCircle, Clock, MapPin, Ban } from 'lucide-react';
import { useDriverRideHistory, useCancelRide, useFareBreakdown } from '@/api-services/hooks/useReports';
import { useDriver, useRiders } from '@/api-services/hooks/useAdminRefactored';
import type { RideHistoryItem } from '@/api-services/hooks/reportsTypes';
import { toast } from 'sonner';
import CancelRideDialog from '@/components/rides/CancelRideDialog';
import { DatePicker } from '@/components/ui/date-picker';
import { Input } from '@/components/ui/input';
import { Search } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { formatGlobalCurrency } from '@/api-services/hooks/useSettings';

const formatCurrency = (amount: number | undefined): string => {
  if (amount === undefined || amount === null) return '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(amount);
};

const parseISODuration = (durationStr: string): number => {
  if (!durationStr || typeof durationStr !== 'string') return 0;
  const match = durationStr.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+(?:\.\d+)?)S)?/);
  if (!match) return 0;
  const hours = parseFloat(match[1] || '0');
  const minutes = parseFloat(match[2] || '0');
  const seconds = parseFloat(match[3] || '0');
  return (hours * 60) + minutes + (seconds / 60);
};

const DriverRideHistoryPage: React.FC = () => {
  const navigate = useNavigate();
  const { driverId } = useParams<{ driverId: string }>();
  const driverIdNum = driverId ? parseInt(driverId, 10) : 0;

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateRange, setDateRange] = useState('year');
  const [customFrom, setCustomFrom] = useState<Date | undefined>(undefined);
  const [customTo, setCustomTo] = useState<Date | undefined>(undefined);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRideId, setSelectedRideId] = useState<number | null>(null);
  const [ride, setRide] = useState<any | null>(null);
  const [isRideDetailsOpen, setIsRideDetailsOpen] = useState(false);

  const { data: fareData, loading: fareLoading } = useFareBreakdown(
  selectedRideId || 0,
  {
    enabled: !!selectedRideId && isRideDetailsOpen,
  }
  );

  const rideDetails = fareData?.data || fareData;

  // Fetch driver info
  const { data: driverData } = useDriver(driverIdNum, { enabled: !!driverIdNum });
  const driverName = driverData?.fullName || driverData?.email || `Driver #${driverIdNum}`;

  // Fetch ride history for this driver via dedicated API
  const { data: rideHistoryResponse, loading: rideHistoryLoading } = useDriverRideHistory(driverIdNum, {
    enabled: !!driverIdNum,
  });

  const rideHistoryData = rideHistoryResponse as any;
  const extractedData = rideHistoryData?.data || rideHistoryData;
  const allItems: RideHistoryItem[] = Array.isArray(extractedData)
    ? extractedData
    : extractedData?.content || [];

  // Collect unique rider IDs from ride history
  const uniqueRiderIds = useMemo(() => {
    const ids = new Set<number>();
    allItems.forEach((ride) => {
      if (ride.riderId) ids.add(ride.riderId);
    });
    return ids;
  }, [allItems]);

  // Fetch riders list to build name lookup
  const { data: ridersResponse } = useRiders(
    { page: 0, size: 1000 },
    { enabled: uniqueRiderIds.size > 0 }
  );

  const riderNameMap = useMemo(() => {
    const map = new Map<number, string>();
    const ridersData = (ridersResponse as any)?.content || [];
    ridersData.forEach((rider: any) => {
      if (rider.riderId) {
        map.set(rider.riderId, rider.fullName || rider.emailId || '');
      }
    });
    return map;
  }, [ridersResponse]);

  const dateFilteredRides = useMemo(() => {
  const now = new Date();

  return allItems.filter((ride) => {
    if (!ride.requestedTime) return false;

    const rideDate = new Date(ride.requestedTime);

    switch (dateRange) {
      case 'today':
        return rideDate.toDateString() === now.toDateString();

      case 'week': {
        const startOfWeek = new Date();
        startOfWeek.setDate(now.getDate() - now.getDay());
        startOfWeek.setHours(0, 0, 0, 0);
        return rideDate >= startOfWeek && rideDate <= now;
      }

      case 'month':
        return (
          rideDate.getMonth() === now.getMonth() &&
          rideDate.getFullYear() === now.getFullYear()
        );

      case 'quarter': {
        const currentQuarter = Math.floor(now.getMonth() / 3);
        const rideQuarter = Math.floor(rideDate.getMonth() / 3);
        return (
          currentQuarter === rideQuarter &&
          rideDate.getFullYear() === now.getFullYear()
        );
      }

      case 'year':
        return rideDate.getFullYear() === now.getFullYear();

        case 'custom':
  if (!customFrom || !customTo) return true;

  const fromDate = new Date(customFrom);
  fromDate.setHours(0, 0, 0, 0);

  const toDate = new Date(customTo);
  toDate.setHours(23, 59, 59, 999);

  return rideDate >= fromDate && rideDate <= toDate;

      default:
        return true;
    }
  });
}, [allItems, dateRange, customFrom, customTo]);

  // Client-side status filter
const filteredRides = useMemo(() => {
  if (statusFilter === 'all') return dateFilteredRides;

  return dateFilteredRides.filter(
    (ride) =>
      (ride.status || '').toLowerCase() === statusFilter.toLowerCase()
  );
}, [dateFilteredRides, statusFilter]);

const searchFilteredRides = useMemo(() => {
  if (!searchTerm.trim()) return filteredRides;

  const term = searchTerm.toLowerCase();

  return filteredRides.filter((ride) => {
    const rideId = (ride.bookingId || ride.rideId || '').toString();

    const riderName =
      ride.rider?.riderName ||
      ride.riderName ||
      (ride as any).passengerName ||
      '';

    const pickup = ride.riderAddress || '';
    const drop = ride.dropAddress || '';

    const startTime = ride.startedTime
      ? new Date(ride.startedTime).toLocaleString()
      : '';

    const endTime = ride.completedTime
      ? new Date(ride.completedTime).toLocaleString()
      : '';

    const distance = ride.rideDistance
      ? ride.rideDistance.toFixed(2)
      : '';

    const duration = ride.rideDuration || '';

    const fare = (
      ride.actualFare ||
      ride.totalFare ||
      ride.amount ||
      0
    ).toString();

    return (
      rideId.includes(term) ||

      riderName.toLowerCase().includes(term) ||

      pickup.toLowerCase().includes(term) ||
      drop.toLowerCase().includes(term) ||

      startTime.toLowerCase().includes(term) ||
      endTime.toLowerCase().includes(term) ||

      distance.includes(term) ||
      duration.toLowerCase().includes(term) ||

      fare.includes(term)
    );
  });
}, [filteredRides, searchTerm]);

  // Client-side pagination
  // const totalRides = filteredRides.length;
  // const totalPages = Math.max(1, Math.ceil(totalRides / itemsPerPage));
  // const rideHistoryItems = filteredRides.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const totalRides = searchFilteredRides.length;
  const totalPages = Math.max(1, Math.ceil(totalRides / itemsPerPage));
  const rideHistoryItems = searchFilteredRides.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const getStatusBadge = (status: string | undefined) => {
    const statusLower = (status || '').toLowerCase();
    const variants: Record<string, { className: string }> = {
      completed: { className: 'bg-success text-success-foreground' },
      cancelled: { className: 'bg-destructive text-destructive-foreground' },
      in_progress: { className: 'bg-warning text-warning-foreground' },
      'in progress': { className: 'bg-warning text-warning-foreground' },
    };
    return variants[statusLower] || variants.completed;
  };

  // Summary stats (always from all items, not filtered)
  // const stats = useMemo(() => {
  //   const completed = allItems.filter(r => (r.status || '').toLowerCase() === 'completed').length;
  //   const cancelled = allItems.filter(r => (r.status || '').toLowerCase() === 'cancelled').length;
  //   const totalFare = allItems.filter(r => (r.status || '').toLowerCase() === 'completed').reduce((sum, r) => sum + (r.actualFare || r.totalFare || r.amount || 0), 0);
  //   return { total: allItems.length, completed, cancelled, totalFare };
  // }, [allItems]);

  const stats = useMemo(() => {
  const completed = dateFilteredRides.filter(
    r => (r.status || '').toLowerCase() === 'completed'
  ).length;

  const cancelled = dateFilteredRides.filter(
    r => (r.status || '').toLowerCase() === 'cancelled'
  ).length;

  const totalFare = dateFilteredRides
    .filter(r => (r.status || '').toLowerCase() === 'completed')
    .reduce(
      (sum, r) => sum + (r.actualFare || r.totalFare || r.amount || 0),
      0
    );

  return {
    total: dateFilteredRides.length,
    completed,
    cancelled,
    totalFare,
  };
}, [dateFilteredRides]);

  // Cancel ride
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelBookingId, setCancelBookingId] = useState(0);

  const { mutate: cancelRide, loading: isCancelling } = useCancelRide({
    onSuccess: () => {
      toast.success('Ride ended successfully');
      setCancelDialogOpen(false);
      setCancelBookingId(0);
      // Refetch by reloading
      window.location.reload();
    },
    onError: (error) => {
      toast.error(`Failed to end ride: ${error.message}`);
    },
  });

  const handleEndRide = (bookingId: number, remarks: string) => {
    cancelRide({ bookingId, cancelledBy: "admin", remarks });
  };

  const isOngoingRide = (status: string | undefined) => {
    const s = (status || '').toLowerCase();
    return s !== 'completed' && s !== 'cancelled';
  };

  const handleOpenRideDetails = (ride: any) => {
  setRide(ride);
  setSelectedRideId(ride.bookingId);
  setIsRideDetailsOpen(true);
};

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/admin/drivers')}
            className="hover:bg-muted"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-foreground">Driver Ride History</h1>
            <p className="text-muted-foreground mt-1">{driverName} (ID: {driverIdNum})</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <Select value={dateRange} onValueChange={(val) => { setDateRange(val); setCurrentPage(1); if (val !== 'custom') { setCustomFrom(undefined); setCustomTo(undefined); } }}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="today">Today</SelectItem>
              <SelectItem value="week">This Week</SelectItem>
              <SelectItem value="month">This Month</SelectItem>
              <SelectItem value="quarter">This Quarter</SelectItem>
              <SelectItem value="year">This Year</SelectItem>
              <SelectItem value="custom">Custom Range</SelectItem>
            </SelectContent>
          </Select>
          {dateRange === 'custom' && (
            <div className="flex items-center gap-2">
              <DatePicker
                value={customFrom}
                onChange={setCustomFrom}
                placeholder="From"
                maxDate={customTo || new Date()}
                className="w-36"
              />
              <span className="text-muted-foreground text-sm">to</span>
              <DatePicker
                value={customTo}
                onChange={setCustomTo}
                placeholder="To"
                minDate={customFrom}
                maxDate={new Date()}
                className="w-36"
              />
            </div>
          )}
           <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setCurrentPage(1); }}>
          <SelectTrigger className="w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
          </SelectContent>
        </Select>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Rides</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
              <Car className="h-8 w-8 text-chart-primary" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Completed</p>
                <p className="text-2xl font-bold text-success">{stats.completed}</p>
              </div>
              <CheckCircle className="h-8 w-8 text-success" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Cancelled</p>
                <p className="text-2xl font-bold text-destructive">{stats.cancelled}</p>
              </div>
              <XCircle className="h-8 w-8 text-destructive" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Fare</p>
                <p className="text-2xl font-bold">{formatGlobalCurrency(stats.totalFare)}</p>
              </div>
              <Clock className="h-8 w-8 text-chart-accent" />
            </div>
          </CardContent>
        </Card>
      </div>

 <Card>
            <CardContent className="p-6">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    type="search"
                    placeholder="Search by ride ID, passenger, or driver..."
                    className="pl-10"
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setCurrentPage(1); 
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                      }
                    }}
                    autoComplete="off"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
      {/* Ride History Table */}
      <Card>
        <CardHeader>
          <CardTitle>Rides ({totalRides})</CardTitle>
        </CardHeader>
        <CardContent>
          {rideHistoryLoading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : rideHistoryItems.length === 0 ? (
            <div className="flex items-center justify-center h-64 text-muted-foreground">
              <p>No ride history found for this driver</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Ride ID</TableHead>
                      <TableHead>Passenger</TableHead>
                      <TableHead>Route</TableHead>
                      <TableHead>Ride Start</TableHead>
                      <TableHead>Ride End</TableHead>
                      <TableHead>Distance / Duration</TableHead>
                      <TableHead>Fare</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Payment Details</TableHead>
                      <TableHead>Ratings</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rideHistoryItems.map((ride: RideHistoryItem) => {
                      const statusBadge = getStatusBadge(ride.status);
                      const fare = ride.actualFare || ride.totalFare || ride.amount || 0;
                      const riderName =
                        ride.riderName ||
                        (ride as any).passengerName ||
                        (ride.riderId && riderNameMap.get(ride.riderId)) ||
                        (ride.riderId ? `Rider #${ride.riderId}` : 'N/A');
                      const rideBookingId = ride.bookingId || ride.rideId || 0;

                      return (
                        <TableRow key={rideBookingId || Math.random()} className="hover:bg-muted/50">
                          {/* <TableCell className="font-medium">#{rideBookingId || 'N/A'}</TableCell> */}

                          <TableCell className="font-medium text-primary cursor-pointer underline">
  <span
    onClick={() => handleOpenRideDetails(ride)}
  >
    #{rideBookingId || "N/A"}
  </span>
</TableCell>
                          <TableCell>
                            <div className="flex items-center space-x-2">
                              <Avatar className="w-6 h-6">
                                <AvatarFallback className="text-xs">
                                  {riderName.split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase()}
                                </AvatarFallback>
                              </Avatar>
                              <span className="text-sm">{riderName}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <TooltipProvider>
                              <div className="max-w-48">
                                {ride.riderAddress && (
                                  <UITooltip>
                                    <TooltipTrigger asChild>
                                      <div className="flex items-center text-sm cursor-default">
                                        <MapPin className="w-3 h-3 mr-1 shrink-0 text-success" />
                                        <span className="truncate">{ride.riderAddress}</span>
                                      </div>
                                    </TooltipTrigger>
                                    <TooltipContent side="top" className="max-w-xs">
                                      <p>{ride.riderAddress}</p>
                                    </TooltipContent>
                                  </UITooltip>
                                )}
                                {ride.dropAddress && (
                                  <UITooltip>
                                    <TooltipTrigger asChild>
                                      <div className="flex items-center text-sm text-muted-foreground cursor-default">
                                        <MapPin className="w-3 h-3 mr-1 shrink-0 text-destructive" />
                                        <span className="truncate">{ride.dropAddress}</span>
                                      </div>
                                    </TooltipTrigger>
                                    <TooltipContent side="top" className="max-w-xs">
                                      <p>{ride.dropAddress}</p>
                                    </TooltipContent>
                                  </UITooltip>
                                )}
                              </div>
                            </TooltipProvider>
                          </TableCell>
                          <TableCell>
                            <div className="text-sm whitespace-nowrap">
                              {ride.startedTime ? new Date(ride.startedTime).toLocaleString() : '-'}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="text-sm whitespace-nowrap">
                              {ride.completedTime ? new Date(ride.completedTime).toLocaleString() : '-'}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="space-y-1">
                              <div className="text-sm">{ride.rideDistance ? `${ride.rideDistance.toFixed(2)} km` : '-'}</div>
                              <div className="text-sm text-muted-foreground">{ride.rideDuration ? `${parseISODuration(String(ride.rideDuration)).toFixed(1)} min` : '-'}</div>
                            </div>
                          </TableCell>
                          <TableCell>{formatGlobalCurrency(fare)}</TableCell>
                          <TableCell>
                            <Badge className={statusBadge.className}>
                              {(ride.status || '').charAt(0).toUpperCase() + (ride.status || '').slice(1)}
                            </Badge>
                          </TableCell>
                             <TableCell>
                            <div className="space-y-1">
                              <div className="text-sm">{ride.paymentType ? ride.paymentType : '-'}</div>
                              <div className="text-sm text-muted-foreground">{ride.paymentStatus ? ride.paymentStatus : '-'}</div>
                            </div>
                          </TableCell>
                           <TableCell>
  <div className="space-y-1">
    <div className="text-sm">
      Driver: {ride.ratingByDriver ?? '-'}
    </div>
    <div className="text-sm text-muted-foreground">
      Rider: {ride.ratingByRider ?? '-'}
    </div>
  </div>
</TableCell>
                         
                          <TableCell className="text-right">
                            {isOngoingRide(ride.status) && rideBookingId ? (
                              <Button
                                variant="destructive"
                                size="sm"
                                onClick={() => {
                                  setCancelBookingId(rideBookingId);
                                  setCancelDialogOpen(true);
                                }}
                              >
                                <Ban className="w-3 h-3 mr-1" />
                                End Ride
                              </Button>
                            ) : (
                              <span className="text-muted-foreground text-sm">-</span>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>

              {totalRides > 0 && (
                <DataTablePagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalItems={totalRides}
                  itemsPerPage={itemsPerPage}
                  onPageChange={setCurrentPage}
                  onItemsPerPageChange={(n) => { setItemsPerPage(n); setCurrentPage(1); }}
                  className="mt-4"
                />
              )}
            </>
          )}
        </CardContent>
      </Card>

      <CancelRideDialog
        open={cancelDialogOpen}
        onOpenChange={setCancelDialogOpen}
        bookingId={cancelBookingId}
        onConfirm={handleEndRide}
        isLoading={isCancelling}
      />

   
    <Dialog open={isRideDetailsOpen} onOpenChange={setIsRideDetailsOpen}>
  <DialogContent className="max-w-5xl">
     <DialogHeader>
            <DialogTitle></DialogTitle>
          </DialogHeader>
    <div className="border rounded-xl p-4 shadow-sm bg-muted/30 mb-4">
  <div className="flex items-center justify-between">

    {/* LEFT → Title */}
    <div>
      <p className="text-lg font-semibold">
        Ride Details #{selectedRideId}
      </p>
    </div>

    {/* RIGHT → Status Badge */}
    <div>
      <span
        className={`px-3 py-1 rounded-full text-xs font-medium capitalize ${
          ride?.status === "COMPLETED"
            ? "bg-green-100 text-green-700"
            : ride?.status === "CANCELLED"
            ? "bg-red-100 text-red-700"
            : "bg-yellow-100 text-yellow-700"
        }`}
      >
        {ride?.status || "N/A"}
      </span>
    </div>

  </div>

  {/* Divider */}
  <hr className="my-3" />

  {/* Time Section */}
  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">

    <div>
      <p className="text-muted-foreground">Ride Start</p>
      <p className="font-medium">
        {ride?.startedTime
          ? new Date(ride.startedTime).toLocaleString()
          : "-"}
      </p>
    </div>

    <div>
      <p className="text-muted-foreground">Ride End</p>
      <p className="font-medium">
        {ride?.completedTime
          ? new Date(ride.completedTime).toLocaleString()
          : "-"}
      </p>
    </div>

  </div>

</div>
    {fareLoading ? (
      <div className="flex justify-center py-10">
        <Loader2 className="animate-spin" />
      </div>
    ) : !rideDetails ? (
      <p className="text-center text-muted-foreground">No data found</p>
    ) : (
      <div className="space-y-6 text-sm">

        {/* 🔹 Top Section - Card Layout */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">

          {/* Passenger Info Card */}
          <div className="border rounded-xl p-4 shadow-sm bg-muted/30">
            <p className="font-semibold text-base mb-2">Passenger Info</p>
            <p><span className="text-muted-foreground">Name:</span> {rideDetails.rider?.riderName}</p>
            <p><span className="text-muted-foreground">Phone:</span> {rideDetails.rider?.riderPhoneNo}</p>
            <p><span className="text-muted-foreground">Email:</span> {rideDetails.rider?.email}</p>
          </div>

          {/* Driver Info Card */}
          <div className="border rounded-xl p-4 shadow-sm bg-muted/30">
            <p className="font-semibold text-base mb-2">Driver Info</p>
            <p><span className="text-muted-foreground">Name:</span> {rideDetails.driver?.driverName}</p>
            <p><span className="text-muted-foreground">Phone:</span> {rideDetails.driver?.phoneNumber}</p>
            <p>
              <span className="text-muted-foreground">Vehicle:</span>{" "}
              {rideDetails.driver?.vehicleMake} - {rideDetails.driver?.vehicleModel}
            </p>
            <p><span className="text-muted-foreground">Reg No:</span> {rideDetails.driver?.vehicleRegistrationNumber}</p>
          </div>

          {/* Ride Info Card */}
          <div className="border rounded-xl p-4 shadow-sm bg-muted/30">
            <p className="font-semibold text-base mb-2">Ride Info</p>
            <p><span className="text-muted-foreground">Pickup:</span> 
            <TooltipProvider>
              <UITooltip>
                                    <TooltipTrigger asChild>
                                      <div className="flex items-center text-sm cursor-default">
                                        <MapPin className="w-3 h-3 mr-1 shrink-0 text-success" />
                                        <span className="truncate">{ride?.riderAddress}</span>
                                      </div>
                                    </TooltipTrigger>
                                    <TooltipContent side="top" className="max-w-xs">
                                      <p>{ride?.riderAddress}</p>
                                    </TooltipContent>
                                  </UITooltip>
                                  </TooltipProvider>
                                  </p>
                                  {ride?.pitStops?.length > 0 && (
  <div>
    <p className="text-muted-foreground font-medium mb-1">Pitstops:</p>

    <div className="space-y-1">
      {ride.pitStops.map((stop: any, index: number) => {
        const address = stop?.address;
        return (
          <div key={index} className="flex items-start text-sm">
            <span className="mr-2 text-muted-foreground">
              {index + 1}.
            </span>

            <TooltipProvider>
              <UITooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center text-sm cursor-default max-w-[200px]">
                    <span className="truncate">{address}</span>
                  </div>
                </TooltipTrigger>

                <TooltipContent side="top" className="max-w-xs">
                  <p>{address}</p>
                </TooltipContent>
              </UITooltip>
            </TooltipProvider>
          </div>
        );
      })}
    </div>
  </div>
)}
            <p><span className="text-muted-foreground">Drop: </span>
             <TooltipProvider>
              <UITooltip>
                                    <TooltipTrigger asChild>
                                      <div className="flex items-center text-sm cursor-default">
                                        <MapPin className="w-3 h-3 mr-1 shrink-0 text-destructive" />
                                        <span className="truncate">{ride?.dropAddress}</span>
                                      </div>
                                    </TooltipTrigger>
                                    <TooltipContent side="top" className="max-w-xs">
                                      <p>{ride?.dropAddress}</p>
                                    </TooltipContent>
                                  </UITooltip>
                                  </TooltipProvider>
                                  
                                   </p>
            <p><span className="text-muted-foreground">Distance:</span> {rideDetails.rideDistance?.toFixed(2)} km</p>
            <p>
              <span className="text-muted-foreground">Duration:</span>{" "}
              {rideDetails.rideDuration?.toFixed(1)} min
            </p>
            <p><span className="text-muted-foreground">Rate/km:</span> {formatGlobalCurrency(rideDetails.ratePerKm)}</p>
          </div>

        </div>

        <hr />

        {/* <div>
          <p className="font-semibold text-base">Fare Breakdown</p>

          <div className="space-y-1 mt-2">
            <p>Base Fare: ${rideDetails.baseFare}</p>
            <p>Distance Fare: ${rideDetails.ratePerKm} × {rideDetails.rideDistance?.toFixed(2)}</p>
            <p>Surge Fees: ${rideDetails.surgeFees}</p>
            <p>Night Charges: ${rideDetails.nightCharges}</p>
            <p>Platform Fee: ${rideDetails.platformFee}</p>
            <p>Toll Charges: ${rideDetails.tollCharges}</p>
            <p>Other Charges: ${rideDetails.otherCharges}</p>
            <p>Tip: ${rideDetails.tipAmount}</p>
            <p>Promo Discount: -${rideDetails.promoDiscount}</p>
          </div>

          <hr className="my-2" />

          <p className="font-bold text-base">
            Total Fare: ${rideDetails.actualFare?.toFixed(2)}
          </p>
        </div> */}

        {/* 🔹 Fare Details Card */}
        <div className="border rounded-xl p-4 shadow-sm">
          <p className="font-semibold text-base mb-3">Fare Details</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {/* <p>Distance: {rideDetails.rideDistance?.toFixed(2)} km</p> */}
            <p>Base Fare: {formatGlobalCurrency(rideDetails.baseFare)}</p>
            <p>Estimated Fare: {formatGlobalCurrency(rideDetails.estimatedFare)}</p>
            <p>Toll Charges: {formatGlobalCurrency(rideDetails.tollCharges)}</p>
            <p>Platform Fee: {formatGlobalCurrency(rideDetails.platformFee)}</p>
            <p>Promotion Discount: - {formatGlobalCurrency(rideDetails.promoDiscount)}</p>
            <p>Other Charges: {formatGlobalCurrency(rideDetails.otherCharges)}</p>
            {rideDetails.pitStopFare!=0.0 && <p>Pit Stop Fare: {formatGlobalCurrency(rideDetails.pitStopFare)}</p>}            
            {rideDetails.nightCharges!= 0.0 && <p>Night Charges: {formatGlobalCurrency(rideDetails.nightCharges)}</p>}            
            {rideDetails.surgeFees!=0.0 && <p>Surge Fee: {formatGlobalCurrency(rideDetails.surgeFees)}</p>}            
            <p>Your Earnings: {formatGlobalCurrency(rideDetails.actualFare - rideDetails.platformFee)}</p>
          </div>

          <hr className="my-3" />

          <p className="font-bold text-lg text-blue-600">
            Total Fare: ${rideDetails.actualFare?.toFixed(2)}
          </p>
          
        </div>

      </div>
    )}
  </DialogContent>
</Dialog>

    </div>
  );
};

export default DriverRideHistoryPage;
